import { useId, useState, type ReactNode } from 'react';
import type { ConsultaDaPlaca, VehiclePlate } from '@core/types';
import { dataHoraBR } from '../lib/formato';
import {
  fichaDaConsulta, formatarPlaca, versoesDaConsulta,
  type TratamentoDaPlaca, type VersaoDaConsulta,
} from '../lib/placa';

// O resultado da busca por placa em DOIS blocos:
//
//   1. O QUE O SISTEMA CONCLUIU — a linha de vehicle_plates, que é com o que a
//      busca guiada logo abaixo trabalha. Quando o carro veio da consulta
//      externa e o documento não determina a versão, a lista das versões
//      possíveis: é a pergunta que o vendedor faz ao cliente.
//   2. O QUE A CONSULTA EXTERNA RETORNOU — o recorte guardado (migração 076)
//      em rótulo e valor, nunca em estrutura de código: identificação, motor,
//      carroceria e as versões da tabela FIPE que ela sugeriu, cada uma
//      marcada pela leitura do sistema.
//
// Pedido do dono em 18/09/2026, com a EMR8H21 na tela: "VW JETTA 2012 — mas
// só, isso para nós não serve". O Jetta 2.0 de 2012 tem quatro versões no
// catálogo, e o combustível que a consulta traz já separa a flex da TSI — mas
// o sistema jogava a resposta fora depois de escolher a família.

const CAMBIO = { AUT: 'automático', MEC: 'manual' } as const;

const TITULO = 'font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-dim';

export default function FichaDaPlaca({ placa, veiculo, tratamento }: {
  placa: string;
  veiculo: VehiclePlate;
  tratamento?: TratamentoDaPlaca | null;
}) {
  const id = useId();
  const consulta = veiculo.consulta_api ?? null;
  const versoes = consulta ? versoesDaConsulta(consulta.dados, tratamento) : [];
  const palpite = veiculo.origem === 'api';

  return (
    <div className="space-y-3">
      <section className="cartao p-4" aria-labelledby={`${id}-sistema`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id={`${id}-sistema`} className={TITULO}>Resultado do sistema</h3>
          <Selo tipo={palpite ? 'alerta' : 'ok'}>
            {palpite ? 'Palpite da consulta externa' : 'Conferido no balcão'}
          </Selo>
        </div>
        <p className="mt-3 font-mono text-xs tracking-widest text-dim">{formatarPlaca(placa)}</p>
        <p className="text-xl font-bold leading-tight">
          {veiculo.montadora} <span className="text-dim">·</span> {veiculo.modelo}
        </p>
        <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
          <Dado rotulo="Ano" valor={veiculo.ano ? String(veiculo.ano) : null} />
          <Dado rotulo="Cilindrada" valor={veiculo.cilindrada} />
          <Dado rotulo="Motor" valor={veiculo.motorizacao} />
        </dl>

        {palpite && tratamento ? (
          <LeituraDoSistema tratamento={tratamento} compativeis={versoes.filter((v) => v.compativel)} />
        ) : null}
        {palpite ? (
          <p className="mt-3 text-xs text-dim">
            Confira com o cliente e corrija abaixo antes de confirmar.
          </p>
        ) : null}
      </section>

      {consulta ? (
        <ConsultaExterna consulta={consulta} versoes={versoes} id={`${id}-consulta`} />
      ) : (
        <p className="px-1 text-xs text-dim">
          {palpite
            ? 'Esta placa foi consultada antes de o sistema guardar o que a consulta externa retorna.'
            : 'Placa identificada no balcão — não há consulta externa guardada para ela.'}
        </p>
      )}
    </div>
  );
}

/** O que o sistema leu no documento, e as versões que sobraram dessa leitura. */
function LeituraDoSistema({ tratamento, compativeis }: {
  tratamento: TratamentoDaPlaca;
  compativeis: VersaoDaConsulta[];
}) {
  const doDocumento = [
    tratamento.litros ? `cilindrada ${tratamento.litros}` : 'cilindrada não informada',
    tratamento.cambio ? `câmbio ${CAMBIO[tratamento.cambio]}` : 'câmbio não informado',
  ].join(' · ');

  return (
    <div className="mt-4 border-t border-edge pt-3">
      <p className="text-sm">
        {compativeis.length === 0
          ? 'Nenhuma versão da tabela FIPE bate com o documento — o sistema ficou só com a família.'
          : compativeis.length === 1
            ? 'O documento determina a versão:'
            : <>O documento não separa estas <strong>{compativeis.length} versões</strong> — pergunte ao cliente:</>}
      </p>
      <p className="mt-0.5 text-xs text-dim">No documento: {doDocumento}.</p>
      {compativeis.length > 0 ? (
        <ul className="mt-2 space-y-1.5">
          {compativeis.map((v) => (
            <li key={v.codigo} className="flex items-start gap-2 text-sm">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-mute" aria-hidden />
              <span>
                <span className="font-medium">{v.nome}</span>
                {v.noCatalogo === false ? (
                  <span className="ml-2 text-xs text-alerta">fora do catálogo da loja</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ConsultaExterna({ consulta, versoes, id }: {
  consulta: ConsultaDaPlaca;
  versoes: VersaoDaConsulta[];
  id: string;
}) {
  const [verFipe, setVerFipe] = useState(false);
  const secoes = fichaDaConsulta(consulta.dados);
  // Data inválida faz o Intl lançar erro — e a ficha não pode derrubar a venda.
  const quando = Number.isFinite(Date.parse(String(consulta.consultada_em)))
    ? dataHoraBR(consulta.consultada_em)
    : null;

  return (
    <section className="cartao p-4" aria-labelledby={id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={id} className={TITULO}>O que a consulta externa retornou</h3>
        {quando ? <span className="text-xs text-dim">consultada em {quando}</span> : null}
      </div>

      {secoes.length > 0 ? (
        <div className="mt-3 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          {secoes.map((s) => (
            <div key={s.titulo}>
              <h4 className="text-xs font-bold uppercase tracking-wide text-mute">{s.titulo}</h4>
              <dl className="mt-1 divide-y divide-edge">
                {s.linhas.map((l) => (
                  <div key={l.rotulo} className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
                    <dt className="text-dim">{l.rotulo}</dt>
                    <dd className="text-right font-medium">{l.valor}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-sm text-dim">A consulta não trouxe dado do veículo além do nome.</p>
      )}

      {versoes.length > 0 ? (
        <div className="mt-4 border-t border-edge pt-3">
          <button type="button" className="botao-terciario"
                  aria-expanded={verFipe} aria-controls={`${id}-fipe`}
                  onClick={() => setVerFipe((v) => !v)}>
            {verFipe ? 'Esconder' : 'Ver'} as {versoes.length} versões da tabela FIPE que a consulta sugeriu
          </button>
          {verFipe ? (
            <ul id={`${id}-fipe`} className="mt-3 divide-y divide-edge">
              {versoes.map((v) => (
                <li key={v.codigo}
                    className={`flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2 text-sm ${
                      v.compativel === false ? 'text-dim' : ''}`}>
                  <span className="font-medium">{v.nome}</span>
                  <span className="flex flex-wrap items-baseline gap-x-3 text-xs">
                    {v.valor ? <span className="text-mute">{v.valor}</span> : null}
                    <MarcaDaVersao versao={v} />
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function MarcaDaVersao({ versao: v }: { versao: VersaoDaConsulta }) {
  if (v.compativel === null) return null;
  if (!v.compativel) return <span>descartada pelo documento</span>;
  return (
    <span className="text-ok">
      compatível com o documento{v.noCatalogo === false ? ' · fora do catálogo' : ''}
    </span>
  );
}

function Selo({ tipo, children }: { tipo: 'ok' | 'alerta'; children: ReactNode }) {
  const cor = tipo === 'ok' ? 'border-ok/50 text-ok' : 'border-alerta/50 text-alerta';
  return <span className={`border px-2 py-0.5 text-xs font-bold ${cor}`}>{children}</span>;
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  if (!valor) return null;
  return (
    <div>
      <dt className={TITULO}>{rotulo}</dt>
      <dd className="text-lg font-semibold">{valor}</dd>
    </div>
  );
}
